import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, Phone, Eye, EyeOff, MapPin, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { authService } from '../utils/apiService';

const Registro = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    nombreLaboratorio: '',
    ubicacion: '',
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Las contraseñas no coinciden.',
        variant: 'destructive',
      });
      setIsLoading(false);
      return;
    }

    try {
      // Concatenamos Lab + Ubicación en el campo nombre_completo_cedula
      // para tener la información disponible en el panel de admin sin cambiar el modelo.
      const response = await authService.register({
        nombre: formData.nombre,
        email: formData.email,
        telefono: formData.telefono,
        nombre_completo_cedula: `${formData.nombreLaboratorio} — ${formData.ubicacion}`,
        password: formData.password
      });

      localStorage.setItem('userToken', response.access_token);
      localStorage.setItem('userName', response.user.nombre);
      localStorage.setItem('userApproved', response.user.is_approved);
      localStorage.setItem('userAdmin', response.user.is_admin);

      toast({
        title: '¡Registro exitoso!',
        description: response.user.is_approved
          ? 'Tu cuenta ha sido creada y aprobada.'
          : 'Tu cuenta ha sido creada. Espera la aprobación del administrador.',
        duration: 5000,
      });
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Error al crear la cuenta.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 py-12 px-4" data-testid="registro-page">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_morabio-web/artifacts/o1ie7x5d_LogoMoraBioNegro.jpg"
            alt="MoraBioVet"
            className="h-20 mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-[#1e3a8a] mb-2">Crear cuenta</h1>
          <p className="text-gray-600">Regístrate en MoraBioVet</p>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-center text-[#1e3a8a]">Registro de Veterinaria</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="registro-form">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del responsable
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type="text"
                    name="nombre"
                    placeholder="Dr. Juan Pérez"
                    value={formData.nombre}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="registro-nombre-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Laboratorio / Veterinaria
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type="text"
                    name="nombreLaboratorio"
                    placeholder="Ej: Clínica Veterinaria San Francisco"
                    value={formData.nombreLaboratorio}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="registro-laboratorio-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ubicación (Google Maps)
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type="text"
                    name="ubicacion"
                    placeholder="Pega aquí tu link de Google Maps"
                    value={formData.ubicacion}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="registro-ubicacion-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Comparte el link de tu ubicación para facilitar la entrega de muestras
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Correo electrónico
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type="email"
                    name="email"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="registro-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type="tel"
                    name="telefono"
                    placeholder="554-554-7772"
                    value={formData.telefono}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="registro-telefono-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 pr-10"
                    required
                    data-testid="registro-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmar contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="registro-confirm-password-input"
                  />
                </div>
              </div>

              <div className="text-sm">
                <label className="flex items-start">
                  <input type="checkbox" className="mr-2 mt-1" required data-testid="registro-terminos-checkbox" />
                  <span className="text-gray-600">
                    Acepto los{' '}
                    <a href="#" className="text-[#1e40af] hover:underline">términos y condiciones</a>
                    {' y la '}
                    <a href="#" className="text-[#1e40af] hover:underline">política de privacidad</a>
                  </span>
                </label>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#84cc16] hover:bg-[#65a30d] text-white py-6 text-lg"
                disabled={isLoading}
                data-testid="registro-submit-button"
              >
                {isLoading ? 'Creando cuenta...' : 'Crear cuenta'}
              </Button>

              <div className="text-center text-sm text-gray-600">
                ¿Ya tienes cuenta?{' '}
                <Link to="/login" className="text-[#1e40af] hover:text-[#1e3a8a] font-semibold">
                  Inicia sesión aquí
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Registro;
